import React from "react";
import { motion } from "framer-motion";
import { Plus, CalendarClock, CalendarDays, Lock, FileBarChart2, ArrowUpRight, Gavel, ArrowRight, Phone } from "lucide-react";
import ReviewItems from "@/components/ReviewItems";
import { Justice } from "@/components/Justice";
import IntegrityCard from "@/components/IntegrityCard";

const TYPE_STYLE = {
  FACTS: { color: "#2E82F6", border: "rgba(46,130,246,0.35)" },
  EVIDENCE: { color: "#10B981", border: "rgba(16,185,129,0.35)" },
  EVENT: { color: "#F59E0B", border: "rgba(245,158,11,0.35)" },
};

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

const CARDS = [
  { key: "new", label: "New Entry", sub: "Add new evidence", Icon: Plus, action: "onNewEntry" },
  { key: "timeline", label: "Timeline", sub: "View events in order", Icon: CalendarClock, view: "timeline" },
  { key: "calendar", label: "Calendar", sub: "Dates & deadlines", Icon: CalendarDays, view: "calendar" },
  { key: "vault", label: "Evidence Vault", sub: "Encrypted locker", Icon: Lock, view: "vault" },
  { key: "reports", label: "Reports", sub: "Create court-ready reports", Icon: FileBarChart2, view: "reports" },
];


function CourtOrderCard({ courtOrder, onNav }) {
  const hasOrder = !!courtOrder?.order_evidence_id;
  const provisions = courtOrder?.provisions || [];
  const confirmed = provisions.filter((p) => p.status === "confirmed").length;
  return (
    <button
      onClick={() => onNav("orders")}
      data-testid="court-order-card"
      className="group w-full text-left border rounded-lg p-5 sm:p-6 transition-colors hover:border-[var(--ek-primary)]"
      style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}
    >
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--ek-primary)" }}>
          <Gavel className="h-5 w-5 text-white" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-head font-semibold tracking-tight">Court Order</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>
            {hasOrder
              ? `${confirmed} of ${provisions.length} provision${provisions.length === 1 ? "" : "s"} confirmed · tap to review consistency`
              : "Do you have an existing court order? Add it to organize its provisions."}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: "var(--ek-primary)" }} />
      </div>
    </button>
  );
}

export default function Home({ user, activity, counts, integrity, courtOrder, isFamily, matterId, matterTitle, onNav, onJump, reviewRefreshKey, onNewEntry }) {
  return (    <div className="space-y-8" data-testid="home-view">
      {/* Greeting + Justice */}
      <div className="border p-6 sm:p-8 ek-scanline relative overflow-hidden" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="ek-stamp inline-block mb-3">{user?.founding_member ? "FOUNDING MEMBER" : "AI DOCUMENTATION COACH"}</div>
            <h1 className="font-head font-bold text-2xl sm:text-3xl tracking-tight leading-tight">
              {user?.founding_member ? `Welcome back, Founder ${user?.name || ""}!` : "Hello! I'm your AI Coach."}
            </h1>
            <p className="text-base mt-1" style={{ color: "var(--ek-text-secondary)" }}>How can I help you document today?</p>
          </div>
          <Justice size={96} className="shrink-0 -mt-2 hidden sm:block" />
        </div>
      </div>

      {/* Primary action cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="action-cards">
        {CARDS.map(({ key, label, sub, Icon, view, action }, i) => (
          <motion.button
            key={key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            onClick={() => (action ? onNewEntry() : onNav(view))}
            data-testid={`card-${key}`}
            className="group text-left border rounded-lg p-5 h-full transition-colors hover:bg-[color:var(--ek-surface-hover)]"
            style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}
          >
            <div className="flex items-center justify-between mb-6">
              <span className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: "var(--ek-primary)" }}>
                <Icon className="h-5 w-5 text-white" />
              </span>
              <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--ek-primary)" }} />
            </div>
            <div className="font-head font-semibold tracking-tight">{label}</div>
            <div className="text-xs mt-1" style={{ color: "var(--ek-text-muted)" }}>
              {key === "vault" ? `${counts.evidence} items stored` : sub}
            </div>
          </motion.button>
        ))}
      </div>

      {/* Case Integrity */}
      {integrity && <IntegrityCard integrity={integrity} matterTitle={matterTitle} onNav={onNav} />}

      {/* Items to Review */}
      {matterId && <ReviewItems matterId={matterId} onJump={onJump} refreshKey={reviewRefreshKey} />}

      {/* Court Order */}
      {isFamily && <CourtOrderCard courtOrder={courtOrder} onNav={onNav} />}

      {/* Parent-Child Communication */}
      {isFamily && (
        <button onClick={() => onNav("comms")} data-testid="comms-card" className="group w-full text-left border rounded-lg p-5 sm:p-6 transition-colors hover:border-[var(--ek-primary)]" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--ek-primary)" }}>
              <Phone className="h-5 w-5 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-head font-semibold tracking-tight">Parent-Child Communication</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>Log calls & texts or import phone records — with neutral activity stats</div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: "var(--ek-primary)" }} />
          </div>
        </button>
      )}

      {/* Recent Activity */}
      <div className="border rounded-lg" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--ek-border)" }}>
          <span className="font-head font-semibold tracking-tight">Recent Activity</span>
          <button onClick={() => onNav("timeline")} data-testid="view-all-activity" className="text-xs transition-colors hover:text-slate-200" style={{ color: "var(--ek-primary)" }}>View all</button>
        </div>
        {activity.length === 0 ? (
          <div className="px-5 py-10 flex flex-col items-center text-center gap-3" data-testid="activity-empty">
            <Justice size={72} />
            <p className="text-sm max-w-xs" style={{ color: "var(--ek-text-secondary)" }}>
              No activity yet. I'm Justice — tell me what happened and I'll help you turn it into a calm, factual record.
            </p>
            <button onClick={onNewEntry} data-testid="empty-new-entry" className="text-sm font-medium transition-colors hover:text-slate-200" style={{ color: "var(--ek-primary)" }}>Add your first entry →</button>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--ek-border)" }} data-testid="activity-feed">
            {activity.map((a) => {
              const s = TYPE_STYLE[a.type] || TYPE_STYLE.FACTS;
              return (
                <div key={a.id + a.type} className="flex items-center gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate" data-private>{a.title || "Untitled"}</div>
                    <div className="ek-mono-label mt-0.5">{fmt(a.at)}</div>
                  </div>
                  <span className="ek-stamp shrink-0" style={{ color: s.color, borderColor: s.border }}>{a.type}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
